/**
 * Groups Service
 * API calls for groups management
 */

import apiClient from '../lib/axios';
import type { Group, GroupDetail } from '../types';

export interface GroupFormData {
  name: string;
  description?: string | null;
  group_logo?: File;
  member_ids?: number[];
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  meta: PaginationMeta;
}

export interface GroupsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  sort?: string;
}

// Image cache for blob URLs
const imageCache = new Map<string, string>();

export const groupsService = {
  /**
   * Get paginated groups with filters
   */
  getPaginated: async (params: GroupsQueryParams = {}): Promise<PaginatedResponse<Group>> => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.set('page', String(params.page));
    if (params.limit) queryParams.set('limit', String(params.limit));
    if (params.search) queryParams.set('search', params.search);
    if (params.sort) queryParams.set('sort', params.sort);

    const queryString = queryParams.toString();
    const url = queryString ? `/groups?${queryString}` : '/groups';

    const { data } = await apiClient.get<PaginatedResponse<Group>>(url);
    return data;
  },

  /**
   * Get all active groups (for dropdowns)
   */
  getAll: async (): Promise<Group[]> => {
    const { data } = await apiClient.get<Group[]>('/groups?all=true');
    return data;
  },

  /**
   * Get group by ID (includes members)
   */
  getById: async (id: number): Promise<GroupDetail> => {
    const { data } = await apiClient.get<GroupDetail>(`/group/${id}`);
    return data;
  },

  /**
   * Check if group name already exists
   */
  checkNameExists: async (name: string, excludeId?: number): Promise<{ exists: boolean }> => {
    const params = new URLSearchParams({
      action: 'check_name',
      name: name.trim(),
    });
    if (excludeId) {
      params.append('exclude_id', String(excludeId));
    }
    const { data } = await apiClient.get<{ exists: boolean }>(`/groups?${params.toString()}`);
    return data;
  },

  /**
   * Create new group
   */
  create: async (groupData: GroupFormData): Promise<Group & { id: number }> => {
    if (groupData.group_logo) {
      const formData = new FormData();
      formData.append('name', groupData.name);
      if (groupData.description) {
        formData.append('description', groupData.description);
      }
      formData.append('group_logo', groupData.group_logo);
      if (groupData.member_ids && groupData.member_ids.length > 0) {
        formData.append('member_ids', JSON.stringify(groupData.member_ids));
      }

      const { data } = await apiClient.post<Group & { id: number }>('/groups', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    } else {
      const payload: Record<string, unknown> = {
        name: groupData.name,
        description: groupData.description || null,
      };
      if (groupData.member_ids && groupData.member_ids.length > 0) {
        payload.member_ids = groupData.member_ids;
      }
      const { data } = await apiClient.post<Group & { id: number }>('/groups', payload);
      return data;
    }
  },

  /**
   * Update group (uses POST with multipart for file uploads, PUT for JSON)
   */
  update: async (id: number, groupData: GroupFormData): Promise<Group> => {
    if (groupData.group_logo) {
      const formData = new FormData();
      formData.append('name', groupData.name);
      if (groupData.description !== undefined) {
        formData.append('description', groupData.description || '');
      }
      formData.append('group_logo', groupData.group_logo);
      if (groupData.member_ids) {
        formData.append('member_ids', JSON.stringify(groupData.member_ids));
      }

      const { data } = await apiClient.post<Group>(`/group/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    } else {
      const { data } = await apiClient.put<Group>(`/group/${id}`, {
        name: groupData.name,
        description: groupData.description ?? null,
        member_ids: groupData.member_ids ?? [],
      });
      return data;
    }
  },

  /**
   * Soft-delete group
   */
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/group/${id}`);
  },

  /**
   * Add member to group
   */
  addMember: async (groupId: number, memberId: number): Promise<void> => {
    await apiClient.post(`/group/${groupId}/members`, { member_id: memberId });
  },

  /**
   * Remove member from group
   */
  removeMember: async (groupId: number, memberId: number): Promise<void> => {
    await apiClient.delete(`/group/${groupId}/members/${memberId}`);
  },

  /**
   * Fetch group logo as blob URL (with JWT authentication)
   */
  fetchGroupImage: async (groupId: number): Promise<string | null> => {
    try {
      const cacheKey = `group_${groupId}`;
      if (imageCache.has(cacheKey)) {
        return imageCache.get(cacheKey)!;
      }

      const response = await apiClient.get<Blob>(`/group_image/${groupId}`, {
        responseType: 'blob',
        params: { t: Date.now() },
      });

      const blobUrl = URL.createObjectURL(response.data);
      imageCache.set(cacheKey, blobUrl);
      return blobUrl;
    } catch (error) {
      console.error(`Failed to load image for group ${groupId}:`, error);
      return null;
    }
  },

  /**
   * Revoke blob URL and remove from cache
   */
  revokeGroupImage: (groupId: number): void => {
    const cacheKey = `group_${groupId}`;
    const blobUrl = imageCache.get(cacheKey);
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      imageCache.delete(cacheKey);
    }
  },

  /**
   * Clear all cached group images
   */
  clearGroupImageCache: (): void => {
    imageCache.forEach((blobUrl) => {
      URL.revokeObjectURL(blobUrl);
    });
    imageCache.clear();
  },
};
